/**
 * PrivateRoute - Protege rutas que requieren autenticación
 * Usa Firebase Authentication para verificar el estado de la sesión
 */

import React, { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import { Box, CircularProgress, Typography } from "@mui/material";

export default function PrivateRoute({ children }) {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let resolved = false;

    // Hot path: si ya hay user_id en localStorage, autenticamos sin esperar a Firebase.
    // Mantenemos el listener para corregir si Firebase dice otra cosa.
    if (localStorage.getItem("user_id")) {
      setIsAuthenticated(true);
      setLoading(false);
      resolved = true;
    }

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      const hasUserId = !!localStorage.getItem("user_id");
      const hasFirebaseUser = !!firebaseUser;
      setIsAuthenticated(hasFirebaseUser || hasUserId);
      setLoading(false);
      resolved = true;
    });

    // Timeout de seguridad: si Firebase no responde en 4s, asumimos no
    // autenticado y mandamos a /login en vez de quedar atascados con spinner.
    const timeout = setTimeout(() => {
      if (!resolved) {
        console.warn("[PrivateRoute] Firebase auth timeout (4s) — redirigiendo a login");
        const hasUserId = !!localStorage.getItem("user_id");
        setIsAuthenticated(hasUserId);
        setLoading(false);
      }
    }, 4000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  // Mostrar loading mientras verificamos autenticación
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)",
        }}
      >
        <CircularProgress sx={{ color: "#6366f1", mb: 2 }} />
        <Typography sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
          Verificando sesión...
        </Typography>
      </Box>
    );
  }

  // Si no está autenticado, redirigir a login guardando la ruta actual
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}
